import { Controller } from '@hotwired/stimulus'
import '../../entrypoints/modules/semantic-ui-dropdown'
import 'jquery-ui/ui/widgets/sortable'
import { dispatchCustomEvent } from '../../modules/utils/DispatchCustomEvent'
import { getTimeFormat } from '../../utils/utils'
import { configureCancellableDateTimePicker } from '../../utils/dateTimePicker'
import { trans, PLURAL_DAYS, PLURAL_HOURS, PLURAL_MINUTES, DAY } from '../../translator'
import 'tempusdominus-bootstrap-4/build/css/tempusdominus-bootstrap-4.min.css'
import moment from 'moment-timezone'

declare const $: any
declare const accountIsUseAmPm: boolean
declare const accountDateFormat: string

;(global as any).moment = moment
require('tempusdominus-bootstrap-4')

export default class extends Controller {
  static values = {
    index: Number,
    sortable: { type: Boolean, default: false },
    logStartsAt: { type: String, default: '' },
    logTimezone: { type: String, default: '' },
    numberOfDays: { type: Number, default: 1 },
    isApplyToAllDays: { type: Boolean, default: true }
  }

  static targets = ['container', 'template', 'dayDropdown']

  declare indexValue: number
  declare sortableValue: boolean
  declare logStartsAtValue: string
  declare logTimezoneValue: string
  declare numberOfDaysValue: number
  declare isApplyToAllDaysValue: boolean
  declare readonly containerTarget: HTMLElement
  declare readonly templateTarget: HTMLElement
  declare readonly dayDropdownTargets: HTMLElement[]

  private updateNumberOfDaysHandler!: (event: Event) => void
  private isApplyToAllDaysHandler!: (event: Event) => void
  private markerAddedHandler!: (event: Event) => void
  private markerRemovedHandler!: (event: Event) => void

  connect (): void {
    this.updateNumberOfDaysHandler = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (detail.startsAt) this.logStartsAtValue = detail.startsAt
      if (detail.timezone) this.logTimezoneValue = detail.timezone
      this.updateNumberOfDays(detail.numberOfDays)
    }
    this.isApplyToAllDaysHandler = (event: Event) => {
      const detail = (event as CustomEvent).detail
      this.isApplyToAllDaysValue = detail.isApplyToAllDays
      this.updateNumberOfDays(this.numberOfDaysValue)
    }
    document.addEventListener('markers:date-range:updated', this.updateNumberOfDaysHandler)
    document.addEventListener('markers:all-days-mode:updated', this.isApplyToAllDaysHandler)

    this.markerAddedHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      try {
        const markersTabEl = this.element.closest('[data-controller*="log-markers-tab"]')
        const scope = markersTabEl || document
        const dayKnown = detail.day !== undefined && detail.day !== null
        const selector = dayKnown
          ? `.marker-group[data-day="${detail.day}"] .marker-row[data-log-marker-id="${detail.logMarkerId}"]`
          : `.marker-row[data-log-marker-id="${detail.logMarkerId}"]`
        const markerRow = scope.querySelector(selector) as HTMLElement | null
        if (markerRow) markerRow.dataset.markerName = detail.markerName
      } catch (_) {
        const markerRow = document.querySelector(`.marker-row[data-log-marker-id="${detail.logMarkerId}"]`) as HTMLElement | null
        if (markerRow) markerRow.dataset.markerName = detail.markerName
      }
      this.addMarkerToRelativePickers(detail.logMarkerId, detail.markerName, this.isPerDayMode ? detail.day : null)
    }
    this.markerRemovedHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail.markerId && !detail.isMarkerRowRemoved) return
      this.removeMarkerFromRelativePickers(detail.logMarkerId, this.isPerDayMode ? detail.day : null)
    }
    document.addEventListener('markers:marker:added', this.markerAddedHandler)
    document.addEventListener('markers:marker:removed', this.markerRemovedHandler)

    const markersTabEl = this.element.closest('[data-controller*="log-markers-tab"]')
    const perDayRows = markersTabEl
      ? Array.from(markersTabEl.querySelectorAll('.trigger-day-section .trigger-row'))
      : []
    const allRows = [...Array.from(this.containerTarget.querySelectorAll('.trigger-row')), ...perDayRows] as HTMLElement[]

    allRows.forEach((row) => { this.initRow(row) })
    allRows.forEach((row) => {
      if (row.dataset.triggerId) {
        this.disableTriggerOption(row.dataset.triggerId, row)
        this.addTriggerToRelativePickers(row.dataset.triggerId, this.getTriggerName(row), row)
        // Emitted on both logs and templates so existing checklist/integration anchor
        // dropdowns pick up this trigger; newly added rows scan the DOM themselves.
        const dayInput = row.querySelector('.trigger-day-input') as HTMLInputElement | null
        dispatchCustomEvent('triggers:trigger:configured', {
          triggerId: row.dataset.triggerId,
          triggerName: this.getTriggerName(row),
          day: dayInput ? dayInput.value : '0'
        })
      }
    })
    allRows.forEach((row) => {
      if (row.dataset.triggerDirection === 'before' || row.dataset.triggerDirection === 'after') {
        this.computeRelativeOffset(row)
      }
    })
    allRows.forEach((row) => {
      if (row.dataset.triggerDirection === 'before' || row.dataset.triggerDirection === 'after') {
        this.computeRelativeOffset(row)
      }
    })

    if (this.sortableValue) {
      $(this.containerTarget).sortable({
        handle: '.handle',
        update: () => this.updateCustomOrder()
      })
    }
  }

  disconnect (): void {
    document.removeEventListener('markers:date-range:updated', this.updateNumberOfDaysHandler)
    document.removeEventListener('markers:all-days-mode:updated', this.isApplyToAllDaysHandler)
    document.removeEventListener('markers:marker:added', this.markerAddedHandler)
    document.removeEventListener('markers:marker:removed', this.markerRemovedHandler)
  }

  get isPerDayMode (): boolean {
    if (this.numberOfDaysValue <= 1 || this.isApplyToAllDaysValue) return false
    try {
      const markersTabEl = this.element.closest('[data-controller*="log-markers-tab"]')
      return !!(markersTabEl?.querySelector('.trigger-day-rows'))
    } catch (_) {
      return false
    }
  }

  get searchScope (): Element {
    if (this.isPerDayMode) {
      return this.element.closest('[data-controller*="log-markers-tab"]') || this.containerTarget
    }
    return this.containerTarget
  }

  add (event: Event): void {
    if (event) event.preventDefault()

    const html = this.templateTarget.innerHTML.replace(/__name__/g, String(this.indexValue))
    this.containerTarget.insertAdjacentHTML('beforeend', html)

    const newRow = this.containerTarget.lastElementChild as HTMLElement
    this.initRow(newRow)
    this.applyExistingSelectionsToRow(newRow)
    if (this.sortableValue) this.updateCustomOrder()

    this.indexValue++
  }

  addForDay (event: Event): void {
    if (event) event.preventDefault()
    const day = parseInt((event.currentTarget as HTMLElement).dataset.day ?? '0')

    const markersTabEl = this.element.closest('[data-controller*="log-markers-tab"]')
    const dayRowsContainer = markersTabEl?.querySelector(`.trigger-day-rows[data-day="${day}"]`)
    if (!dayRowsContainer) return

    const html = this.templateTarget.innerHTML.replace(/__name__/g, String(this.indexValue))
    dayRowsContainer.insertAdjacentHTML('beforeend', html)
    const newRow = dayRowsContainer.lastElementChild as HTMLElement

    const dayInput = newRow.querySelector('.trigger-day-input') as HTMLInputElement | null
    if (dayInput) dayInput.value = String(day)

    this.initRow(newRow)
    this.applyExistingSelectionsToRow(newRow)

    const dayDropdownContainer = newRow.querySelector('.trigger-day-dropdown-container')
    if (dayDropdownContainer) dayDropdownContainer.classList.add('d-none')
    this.indexValue++
  }

  remove (event: Event): void {
    const row = (event.currentTarget as HTMLElement).closest('.trigger-row') as HTMLElement
    if (row.dataset.triggerId) {
      this.enableTriggerOption(row.dataset.triggerId, row)
      this.removeTriggerFromRelativePickers(row.dataset.triggerId, row)
      const removeDayInput = row.querySelector('.trigger-day-input') as HTMLInputElement | null
      dispatchCustomEvent('triggers:trigger:cleared', { triggerId: row.dataset.triggerId, day: removeDayInput ? removeDayInput.value : '0' })
    }
    row.remove()
    if (this.sortableValue) this.updateCustomOrder()
  }

  typeChange (event: { currentTarget: { value: string, closest: (selector: string) => HTMLElement } }): void {
    const row = event.currentTarget.closest('.trigger-row')
    const isAbsolute = event.currentTarget.value === 'at'

    row.querySelectorAll('.relative-group').forEach((el) => {
      (el as HTMLElement).classList.toggle('d-none', isAbsolute);
      (el as HTMLElement).classList.toggle('d-inline', !isAbsolute)
    })
    row.querySelector('.absolute-fields')?.classList.toggle('d-none', !isAbsolute)
    row.querySelector('.absolute-fields')?.classList.toggle('d-inline', isAbsolute)
  }

  updateCustomOrder (): void {
    this.containerTarget.querySelectorAll('.trigger-row').forEach((row, index) => {
      const input = row.querySelector('.custom-order-input') as HTMLInputElement | null
      if (input) input.value = String(index)
    })
  }

  computeRelativeOffset (row: HTMLElement): void {
    let days = 0; let hours = 0; let minutes = 0

    ;[
      { selector: '.trigger-days-input', localiseKey: 'plural_days', unit: PLURAL_DAYS },
      { selector: '.trigger-hours-input', localiseKey: 'plural_hours', unit: PLURAL_HOURS },
      { selector: '.trigger-minutes-input', localiseKey: 'plural_minutes', unit: PLURAL_MINUTES }
    ].forEach(({ selector, localiseKey, unit }) => {
      const input = row.querySelector(selector) as HTMLInputElement | null
      if (!input) return
      const value = parseInt(input.value) || 0
      const clamped = Math.min(Math.max(value, parseInt(input.min) || 0), parseInt(input.max) || 999)
      input.value = String(clamped)
      const label = input.nextElementSibling as HTMLElement | null
      if (label && label.dataset.localiseKey === localiseKey) {
        label.textContent = trans(unit, { '%count%': clamped }, 'logs')
      }
      if (localiseKey === 'plural_days') days = clamped
      else if (localiseKey === 'plural_hours') hours = clamped
      else minutes = clamped
    })

    const sign = row.dataset.triggerDirection === 'before' ? -1 : 1
    const signedOffset = (days * 1440 + hours * 60 + minutes) * sign

    const offsetInput = row.querySelector('.trigger-offset-input') as HTMLInputElement | null
    if (offsetInput) offsetInput.value = String(signedOffset)

    const relativeInput = row.querySelector('.trigger-relative-input') as HTMLInputElement | null
    const referenceTriggerId = relativeInput ? relativeInput.value : null
    const relativeEventMarkerInput = row.querySelector('.trigger-relative-event-marker-input') as HTMLInputElement | null
    const referenceEventMarkerId = relativeEventMarkerInput ? relativeEventMarkerInput.value : null

    if (referenceTriggerId) {
      const referenceRow = this.searchScope.querySelector(`.trigger-row[data-trigger-id="${referenceTriggerId}"]`) as HTMLElement | null
      if (referenceRow) {
        const refOccursAtInMinutes = parseInt((referenceRow.querySelector('.trigger-time-input') as HTMLInputElement | null)?.value ?? '')
        const timeInput = row.querySelector('.trigger-time-input') as HTMLInputElement | null
        if (timeInput) timeInput.value = String(isNaN(refOccursAtInMinutes) ? signedOffset : refOccursAtInMinutes + signedOffset)
      }
    } else if (referenceEventMarkerId) {
      const markerRow = document.querySelector(`.marker-row[data-log-marker-id="${referenceEventMarkerId}"]`) as HTMLElement | null
      if (markerRow) {
        const refOccursAtInMinutes = parseInt(markerRow.dataset.occursAtInMinutes ?? '')
        const timeInput = row.querySelector('.trigger-time-input') as HTMLInputElement | null
        if (timeInput) timeInput.value = String(isNaN(refOccursAtInMinutes) ? signedOffset : refOccursAtInMinutes + signedOffset)
      }
    }

    this.recalculateAllRelativeTriggers()
  }

  // Switching a trigger to an absolute ("at") time detaches it from any relative anchor.
  // Drop the relative reference and re-derive the time purely from the absolute picker.
  // Otherwise the time computed while the row was relative lingers in the hidden input
  // (invisible in the UI, since the absolute field renders empty) and is saved as the
  // trigger's absolute time — and the stale relative reference, which takes precedence
  // over occursAtInMinutes when the log is created, would re-introduce a time too.
  applyAbsoluteTime (row: HTMLElement): void {
    const triggerInput = row.querySelector('.trigger-relative-input') as HTMLInputElement | null
    if (triggerInput) triggerInput.value = ''
    const eventMarkerInput = row.querySelector('.trigger-relative-event-marker-input') as HTMLInputElement | null
    if (eventMarkerInput) eventMarkerInput.value = ''
    const relativeSelect = row.querySelector('.trigger-relative-select')
    if (relativeSelect) $(relativeSelect).dropdown('clear')

    const timeInput = row.querySelector('.trigger-time-input') as HTMLInputElement | null
    if (timeInput) {
      const timePicker = row.querySelector('.trigger-datetimepicker') as HTMLElement | null
      const date = timePicker && $.fn.datetimepicker ? $(timePicker).datetimepicker('date') : null
      timeInput.value = date ? String(date.hours() * 60 + date.minutes()) : ''
    }

    this.recalculateAllRelativeTriggers()
  }

  recalculateAllRelativeTriggers (): void {
    [0, 1].forEach(() => {
      this.searchScope.querySelectorAll('.trigger-row').forEach((row) => {
        const el = row as HTMLElement
        if (el.dataset.triggerDirection !== 'before' && el.dataset.triggerDirection !== 'after') return

        const relativeInput = el.querySelector('.trigger-relative-input') as HTMLInputElement | null
        const referenceTriggerId = relativeInput ? relativeInput.value : null
        const relativeEventMarkerInput = el.querySelector('.trigger-relative-event-marker-input') as HTMLInputElement | null
        const referenceEventMarkerId = relativeEventMarkerInput ? relativeEventMarkerInput.value : null

        const signedOffset = parseInt((el.querySelector('.trigger-offset-input') as HTMLInputElement | null)?.value ?? '') || 0
        const timeInput = el.querySelector('.trigger-time-input') as HTMLInputElement | null

        if (referenceTriggerId) {
          const referenceRow = this.searchScope.querySelector(`.trigger-row[data-trigger-id="${referenceTriggerId}"]`) as HTMLElement | null
          if (!referenceRow) return
          const refOccursAtInMinutes = parseInt((referenceRow.querySelector('.trigger-time-input') as HTMLInputElement | null)?.value ?? '')
          if (timeInput) timeInput.value = String(isNaN(refOccursAtInMinutes) ? signedOffset : refOccursAtInMinutes + signedOffset)
        } else if (referenceEventMarkerId) {
          const markerRow = document.querySelector(`.marker-row[data-log-marker-id="${referenceEventMarkerId}"]`) as HTMLElement | null
          if (!markerRow) return
          const refOccursAtInMinutes = parseInt(markerRow.dataset.occursAtInMinutes ?? '')
          if (timeInput) timeInput.value = String(isNaN(refOccursAtInMinutes) ? signedOffset : refOccursAtInMinutes + signedOffset)
        }
      })
    })

    if (!this.sortableValue) this.reorderTriggers()

    // Trigger times changed: let dependent pickers (checklist / integration anchors)
    // refresh their displayed times reactively rather than only on focus/click.
    dispatchCustomEvent('triggers:times:updated', {})
  }

  reorderTriggers (): void {
    const sortRows = (rows: HTMLElement[], parent: Element): void => {
      const getTime = (row: HTMLElement): number => {
        const value = parseInt((row.querySelector('.trigger-time-input') as HTMLInputElement | null)?.value ?? '')
        return isNaN(value) ? Number.POSITIVE_INFINITY : value
      }

      // A relative trigger anchored to another trigger must keep its side of that
      // anchor regardless of computed time: "after" sorts below the anchor, "before"
      // above it. This matters when the offset is 0 (or any offset producing an equal
      // time), where sorting on occursAtInMinutes alone would leave the order undefined.
      const rowsById = new Map<string, HTMLElement>()
      rows.forEach((row) => { if (row.dataset.triggerId) rowsById.set(row.dataset.triggerId, row) })

      const predecessors = new Map<HTMLElement, Set<HTMLElement>>()
      rows.forEach((row) => predecessors.set(row, new Set<HTMLElement>()))
      rows.forEach((row) => {
        const direction = row.dataset.triggerDirection
        if (direction !== 'before' && direction !== 'after') return
        const anchorId = (row.querySelector('.trigger-relative-input') as HTMLInputElement | null)?.value
        if (!anchorId) return
        const anchor = rowsById.get(anchorId)
        if (!anchor || anchor === row) return
        if (direction === 'after') {
          predecessors.get(row)?.add(anchor)
        } else {
          predecessors.get(anchor)?.add(row)
        }
      })

      // Stable topological order: repeatedly emit the row whose anchor constraints are
      // already satisfied and whose time is smallest, breaking ties by original order.
      const originalIndex = new Map<HTMLElement, number>()
      rows.forEach((row, index) => originalIndex.set(row, index))

      const emitted = new Set<HTMLElement>()
      const ordered: HTMLElement[] = []
      while (ordered.length < rows.length) {
        const ready = rows.filter((row) => {
          if (emitted.has(row)) return false
          return Array.from(predecessors.get(row) ?? []).every((p) => emitted.has(p))
        })
        // Guard against cyclic anchors so the loop always terminates.
        const candidates = ready.length > 0 ? ready : rows.filter((row) => !emitted.has(row))
        candidates.sort((a, b) => {
          const timeDiff = getTime(a) - getTime(b)
          if (timeDiff !== 0) return timeDiff
          return (originalIndex.get(a) ?? 0) - (originalIndex.get(b) ?? 0)
        })
        const next = candidates[0]
        emitted.add(next)
        ordered.push(next)
      }

      ordered.forEach(row => parent.appendChild(row))
    }

    if (this.isPerDayMode) {
      try {
        const markersTabEl = this.element.closest('[data-controller*="log-markers-tab"]') || this.element
        markersTabEl.querySelectorAll('.trigger-day-rows').forEach((section) => {
          sortRows(Array.from(section.querySelectorAll('.trigger-row')) as HTMLElement[], section)
        })
      } catch (_) { }
      return
    }

    sortRows(Array.from(this.containerTarget.querySelectorAll('.trigger-row')) as HTMLElement[], this.containerTarget)
  }

  applyExistingSelectionsToRow (newRow: HTMLElement): void {
    const newDropdown = newRow.querySelector('.trigger-select')
    if (!newDropdown) return
    this.getTriggerScope(newRow).querySelectorAll('.trigger-row').forEach((existingRow) => {
      const el = existingRow as HTMLElement
      if (el === newRow || !el.dataset.triggerId) return
      newDropdown.querySelectorAll('.menu .item').forEach((option) => {
        if ((option as HTMLElement).dataset.value === el.dataset.triggerId) {
          option.classList.add('disabled')
        }
      })
      this.addTriggerToRelativePickers(el.dataset.triggerId, this.getTriggerName(el), el)
    })
    const rowDayContainer = newRow.closest('.trigger-day-rows') as HTMLElement | null
    if (this.isPerDayMode && rowDayContainer) {
      const triggerDay = parseInt(rowDayContainer.dataset.day ?? '0')
      const markerDay = triggerDay
      try {
        const markersTabEl = this.element.closest('[data-controller*="log-markers-tab"]') || this.element
        markersTabEl.querySelectorAll(`.marker-group[data-day="${markerDay}"] .marker-row`).forEach((markerRow) => {
          const el = markerRow as HTMLElement
          const { logMarkerId } = el.dataset
          if (!logMarkerId) return
          let markerName = el.dataset.markerName
          if (!markerName) {
            const logEventSelect = el.querySelector('.log-event-select')
            markerName = logEventSelect ? $(logEventSelect).dropdown('get text') : ''
          }
          if (markerName) this.addMarkerToRelativePickers(logMarkerId, markerName, markerDay)
        })
      } catch (_) { }
    } else {
      document.querySelectorAll('.marker-row[data-marker-name]').forEach((markerRow) => {
        const el = markerRow as HTMLElement
        const { logMarkerId, markerName } = el.dataset
        if (logMarkerId && markerName) this.addMarkerToRelativePickers(logMarkerId, markerName)
      })
    }
    const relSelect = newRow.querySelector('.trigger-relative-select')
    if (relSelect) $(relSelect).dropdown('refresh')
  }

  ensurePreselectedItemInMenu (relativeSelect: HTMLElement): void {
    const initialValue = relativeSelect.dataset.value
    const anchorType = relativeSelect.dataset.initialAnchorType
    const initialText = relativeSelect.dataset.initialText
    if (!initialValue || !anchorType || !initialText) return

    const menu = relativeSelect.querySelector('.menu')
    if (!menu || menu.querySelector(`.item[data-value="${initialValue}"]`)) return

    const item = document.createElement('div')
    item.className = 'item'
    item.dataset.value = initialValue
    item.dataset.anchorType = anchorType
    item.textContent = initialText
    if (anchorType === 'trigger') {
      menu.appendChild(item)
      const header = menu.querySelector('.trigger-anchor-trigger-header')
      if (header) header.classList.remove('d-none')
    } else {
      const divider = menu.querySelector('.trigger-anchor-divider')
      const insertBefore = divider || menu.querySelector('.trigger-anchor-trigger-header')
      if (insertBefore) menu.insertBefore(item, insertBefore)
      else menu.appendChild(item)
      const header = menu.querySelector('.trigger-anchor-marker-header')
      if (header) header.classList.remove('d-none')
    }
  }

  getTriggerName (row: HTMLElement): string {
    const triggerDropdown = row.querySelector('.trigger-select')
    if (!triggerDropdown) return ''
    const selected = triggerDropdown.querySelector(`.menu .item[data-value="${row.dataset.triggerId}"]`)
    return selected ? selected.textContent?.trim() ?? '' : ''
  }

  addTriggerToRelativePickers (triggerId: string, triggerName: string, exceptRow: HTMLElement): void {
    this.getTriggerScope(exceptRow).querySelectorAll('.trigger-relative-select').forEach((dropdown) => {
      if (dropdown.closest('.trigger-row') === exceptRow) return
      const menu = dropdown.querySelector('.menu')
      if (!menu || menu.querySelector(`.item[data-value="${triggerId}"][data-anchor-type="trigger"]`)) return
      const item = document.createElement('div')
      item.className = 'item'
      item.dataset.value = triggerId
      item.dataset.anchorType = 'trigger'
      item.textContent = triggerName
      menu.appendChild(item)
      const header = menu.querySelector('.trigger-anchor-trigger-header')
      if (header) header.classList.remove('d-none')
      $(dropdown).dropdown('refresh')
    })
  }

  removeTriggerFromRelativePickers (triggerId: string, scopeRow: HTMLElement | null = null): void {
    this.getTriggerScope(scopeRow).querySelectorAll('.trigger-relative-select').forEach((dropdown) => {
      const menu = dropdown.querySelector('.menu')
      if (!menu) return
      const item = menu.querySelector(`.item[data-value="${triggerId}"][data-anchor-type="trigger"]`)
      if (!item) return
      if ($(dropdown).dropdown('get value') === triggerId) {
        $(dropdown).dropdown('clear')
        const row = dropdown.closest('.trigger-row')
        if (row) {
          const triggerInput = row.querySelector('.trigger-relative-input') as HTMLInputElement | null
          if (triggerInput) triggerInput.value = ''
        }
      }
      item.remove()
      const hasTriggers = !!menu.querySelector('.item[data-anchor-type="trigger"]')
      const header = menu.querySelector('.trigger-anchor-trigger-header')
      if (header) header.classList.toggle('d-none', !hasTriggers)
      $(dropdown).dropdown('refresh')
    })
  }

  addMarkerToRelativePickers (logMarkerId: string, markerName: string, markerDay: number | null = null): void {
    if (!logMarkerId || !markerName) return
    this.getScopeForMarkerDay(markerDay).querySelectorAll('.trigger-relative-select').forEach((dropdown) => {
      const menu = dropdown.querySelector('.menu')
      if (!menu || menu.querySelector(`.item[data-value="${logMarkerId}"][data-anchor-type="marker"]`)) return
      const divider = menu.querySelector('.trigger-anchor-divider')
      const insertBefore = divider || menu.querySelector('.trigger-anchor-trigger-header')
      const item = document.createElement('div')
      item.className = 'item'
      item.dataset.value = logMarkerId
      item.dataset.anchorType = 'marker'
      item.textContent = markerName
      if (insertBefore) {
        menu.insertBefore(item, insertBefore)
      } else {
        menu.appendChild(item)
      }
      const header = menu.querySelector('.trigger-anchor-marker-header')
      if (header) header.classList.remove('d-none')
      const hasTriggers = !!menu.querySelector('.item[data-anchor-type="trigger"]')
      if (divider && hasTriggers) divider.classList.remove('d-none')
      $(dropdown).dropdown('refresh')
    })
  }

  removeMarkerFromRelativePickers (logMarkerId: string, markerDay: number | null = null): void {
    this.getScopeForMarkerDay(markerDay).querySelectorAll('.trigger-relative-select').forEach((dropdown) => {
      const menu = dropdown.querySelector('.menu')
      if (!menu) return
      const item = menu.querySelector(`.item[data-value="${logMarkerId}"][data-anchor-type="marker"]`)
      if (!item) return
      if ($(dropdown).dropdown('get value') === logMarkerId) {
        $(dropdown).dropdown('clear')
        const row = dropdown.closest('.trigger-row')
        if (row) {
          const eventMarkerInput = row.querySelector('.trigger-relative-event-marker-input') as HTMLInputElement | null
          if (eventMarkerInput) eventMarkerInput.value = ''
        }
      }
      item.remove()
      const hasMarkers = !!menu.querySelector('.item[data-anchor-type="marker"]')
      const hasTriggers = !!menu.querySelector('.item[data-anchor-type="trigger"]')
      const header = menu.querySelector('.trigger-anchor-marker-header')
      const divider = menu.querySelector('.trigger-anchor-divider')
      if (header) header.classList.toggle('d-none', !hasMarkers)
      if (divider) divider.classList.toggle('d-none', !hasMarkers || !hasTriggers)
      $(dropdown).dropdown('refresh')
    })
  }

  getTriggerScope (row: HTMLElement | null): Element {
    if (this.isPerDayMode && row) {
      const dayRows = row.closest('.trigger-day-rows')
      if (dayRows) return dayRows
    }
    return this.searchScope
  }

  getScopeForMarkerDay (markerDay: number | null): Element {
    if (!this.isPerDayMode || markerDay === null || markerDay === undefined) return this.searchScope
    const triggerDay = parseInt(String(markerDay))
    try {
      const markersTabEl = this.element.closest('[data-controller*="log-markers-tab"]') || this.element
      return markersTabEl.querySelector(`.trigger-day-rows[data-day="${triggerDay}"]`) || this.searchScope
    } catch (_) {
      return this.searchScope
    }
  }

  disableTriggerOption (triggerId: string, exceptRow: HTMLElement | null = null): void {
    this.getTriggerScope(exceptRow).querySelectorAll('.trigger-select').forEach((dropdown) => {
      if (dropdown.closest('.trigger-row') === exceptRow) return
      dropdown.querySelectorAll('.menu .item').forEach((option) => {
        if ((option as HTMLElement).dataset.value === triggerId) option.classList.add('disabled')
      })
    })
  }

  enableTriggerOption (triggerId: string, scopeRow: HTMLElement | null = null): void {
    this.getTriggerScope(scopeRow).querySelectorAll('.trigger-select').forEach((dropdown) => {
      dropdown.querySelectorAll('.menu .item').forEach((option) => {
        if ((option as HTMLElement).dataset.value === triggerId) option.classList.remove('disabled')
      })
    })
  }

  initRow (row: HTMLElement): void {
    if (row.dataset.triggerDirection) {
      const isAbsolute = row.dataset.triggerDirection === 'at'
      row.querySelectorAll('.relative-group').forEach((el) => {
        (el as HTMLElement).classList.toggle('d-none', isAbsolute);
        (el as HTMLElement).classList.toggle('d-inline', !isAbsolute)
      })
      const absoluteFields = row.querySelector('.absolute-fields') as HTMLElement | null
      absoluteFields?.classList.toggle('d-none', !isAbsolute)
      absoluteFields?.classList.toggle('d-inline', isAbsolute)
    }

    const triggerDropdown = row.querySelector('.trigger-select')
    if (triggerDropdown) {
      const hiddenInput = triggerDropdown.querySelector('input[type="hidden"]') as HTMLInputElement | null
      if (hiddenInput && hiddenInput.value) {
        row.dataset.triggerId = hiddenInput.value
      }
      $(triggerDropdown).dropdown({
        forceSelection: false,
        onChange: (value: string, text: string) => {
          const previousId = row.dataset.triggerId
          if (previousId) {
            this.enableTriggerOption(previousId, row)
            this.removeTriggerFromRelativePickers(previousId, row)
            const changeDayInput = row.querySelector('.trigger-day-input') as HTMLInputElement | null
            dispatchCustomEvent('triggers:trigger:cleared', { triggerId: previousId, day: changeDayInput ? changeDayInput.value : '0' })
          }
          row.dataset.triggerId = value
          if (value) {
            this.disableTriggerOption(value, row)
            this.addTriggerToRelativePickers(value, text, row)
            const dayInput = row.querySelector('.trigger-day-input') as HTMLInputElement | null
            dispatchCustomEvent('triggers:trigger:configured', {
              triggerId: value,
              triggerName: text,
              day: dayInput ? dayInput.value : '0'
            })
          }
        }
      })
    }

    const typeDropdown = row.querySelector('.trigger-type-select')
    if (typeDropdown) {
      $(typeDropdown).dropdown({
        onChange: (value: string) => {
          const typeInput = row.querySelector('.trigger-type-input') as HTMLInputElement | null
          if (typeInput) typeInput.value = value === 'at' ? 'absolute' : 'relative'
          row.dataset.triggerDirection = value
          this.typeChange({ currentTarget: { value, closest: () => row } })
          if (value === 'at') {
            this.applyAbsoluteTime(row)
          } else {
            this.computeRelativeOffset(row)
          }
        }
      })
    }

    row.querySelectorAll('.trigger-days-input, .trigger-hours-input, .trigger-minutes-input').forEach((input) => {
      input.addEventListener('blur', () => this.computeRelativeOffset(row))
    })

    const relativeSelect = row.querySelector('.trigger-relative-select') as HTMLElement | null
    if (relativeSelect) {
      this.ensurePreselectedItemInMenu(relativeSelect)
      $(relativeSelect).dropdown({
        forceSelection: false,
        onChange: (value: string, text: string, $item: any) => {
          const anchorType = $item ? $item.data('anchor-type') : null
          const triggerInput = row.querySelector('.trigger-relative-input') as HTMLInputElement | null
          const eventMarkerInput = row.querySelector('.trigger-relative-event-marker-input') as HTMLInputElement | null
          if (anchorType === 'trigger') {
            if (triggerInput) triggerInput.value = value
            if (eventMarkerInput) eventMarkerInput.value = ''
          } else if (anchorType === 'marker') {
            if (eventMarkerInput) eventMarkerInput.value = value
            if (triggerInput) triggerInput.value = ''
          } else {
            if (triggerInput) triggerInput.value = ''
            if (eventMarkerInput) eventMarkerInput.value = ''
          }
          this.computeRelativeOffset(row)
        }
      })
      if (relativeSelect.dataset.value) {
        $(relativeSelect).dropdown('set value', relativeSelect.dataset.value)
      }
    }

    const timePicker = row.querySelector('.trigger-datetimepicker') as HTMLElement | null
    if (timePicker && $.fn.datetimepicker) {
      $(timePicker).datetimepicker({
        focusOnShow: false,
        toolbarPlacement: 'bottom',
        format: getTimeFormat(typeof accountIsUseAmPm !== 'undefined' ? accountIsUseAmPm : false),
        buttons: { showClose: true, showClear: true }
      })
      const timeInput = row.querySelector('.trigger-time-input') as HTMLInputElement | null
      configureCancellableDateTimePicker($(timePicker), timePicker.id, (date: any) => {
        if (timeInput) timeInput.value = date ? String(date.hours() * 60 + date.minutes()) : ''
        this.recalculateAllRelativeTriggers()
      })
    }

    const dayDropdownContainer = row.querySelector('.trigger-day-dropdown-container') as HTMLElement | null
    if (dayDropdownContainer) {
      this.initDayDropdown(dayDropdownContainer)
      const inPerDaySection = !!row.closest('.trigger-day-rows')
      const showDropdown = !inPerDaySection && this.numberOfDaysValue > 1 && !this.isApplyToAllDaysValue
      dayDropdownContainer.classList.toggle('d-none', !showDropdown)
    }
  }

  initDayDropdown (container: HTMLElement): void {
    this.populateDayOptions(container)
    const dropdown = container.querySelector('.trigger-day-dropdown')
    const hiddenInput = container.querySelector('.trigger-day-input') as HTMLInputElement | null
    if (!dropdown || !hiddenInput) return
    $(dropdown).dropdown({
      forceSelection: false,
      onChange: (value: string) => {
        hiddenInput.value = value || '0'
        const row = container.closest('.trigger-row') as HTMLElement | null
        if (row && row.dataset.triggerId) {
          dispatchCustomEvent('triggers:trigger:configured', {
            triggerId: row.dataset.triggerId,
            triggerName: this.getTriggerName(row),
            day: hiddenInput.value
          })
        }
      }
    })
    if (hiddenInput.value && hiddenInput.value !== '0') {
      $(dropdown).dropdown('set value', hiddenInput.value)
    }
  }

  populateDayOptions (container: HTMLElement): void {
    const dropdown = container.querySelector('.trigger-day-dropdown')
    if (!dropdown) return
    const menu = dropdown.querySelector('.menu')
    if (!menu) return
    menu.innerHTML = ''
    const numberOfDays = this.numberOfDaysValue || 1
    const logStartsAt = this.logStartsAtValue
    const timezone = this.logTimezoneValue
    for (let day = 0; day < numberOfDays; day++) {
      const item = document.createElement('div')
      item.className = 'item'
      item.dataset.value = String(day)
      let label
      try {
        if (logStartsAt && timezone && moment && typeof moment.utc === 'function') {
          const date = moment.utc(logStartsAt).tz(timezone).add(day, 'days')
          const dateFormat = typeof accountDateFormat !== 'undefined' ? accountDateFormat : 'DD/MM/YYYY'
          label = `${trans(DAY, {}, 'logs')} ${day + 1} (${date.format(dateFormat)})`
        } else {
          label = `${trans(DAY, {}, 'logs')} ${day + 1}`
        }
      } catch (e) {
        label = `${trans(DAY, {}, 'logs')} ${day + 1}`
      }
      item.textContent = label
      menu.appendChild(item)
    }
  }

  updateNumberOfDays (numberOfDays: number): void {
    this.numberOfDaysValue = numberOfDays
    const showDropdown = numberOfDays > 1 && !this.isApplyToAllDaysValue
    this.dayDropdownTargets.forEach((container) => {
      if (container.closest('.trigger-day-rows')) return
      container.classList.toggle('d-none', !showDropdown)
      if (showDropdown) {
        this.populateDayOptions(container)
        const dropdown = container.querySelector('.trigger-day-dropdown')
        if (dropdown) $(dropdown).dropdown('refresh')
      } else {
        const hiddenInput = container.querySelector('.trigger-day-input') as HTMLInputElement | null
        if (hiddenInput) hiddenInput.value = '0'
      }
    })
  }
}
